import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  Car, Menu, X, ChevronRight, ArrowRight,
  Clock, ClipboardList, TrendingDown,
  Upload, Bot, Globe, BarChart3, Smartphone, Shield,
  FileX, Wrench, Rocket,
  Send, MessageCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WA_NUMBER = "5492612509164";
const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent("Hola, me interesa saber sobre el sistema para agencias AutoFlow")}`;

/* ═══════════════════════════════════════════
   DESIGN TOKENS
   ═══════════════════════════════════════════ */
const T = {
  bg: '#050A30',
  bgAlt: '#0A1240',
  bgDeep: '#020818',
  blue: '#0000FF',
  cyan: '#00FFFF',
  white: '#FFFFFF',
  textMuted: '#A0A8C8',
  textLight: '#E8E8F0',
  cardBg: 'rgba(255,255,255,0.04)',
  cardBorder: 'rgba(0,255,255,0.12)',
  font: {
    display: "'Barlow Condensed', sans-serif",
    heading: "'Barlow', sans-serif",
    body: "'Inter', sans-serif",
  }
};

/* ═══════════════════════════════════════════
   ANIMATED SECTION WRAPPER
   ═══════════════════════════════════════════ */
function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ═══════════════════════════════════════════
   SVG ILLUSTRATIONS
   ═══════════════════════════════════════════ */
function DashboardSVG() {
  return (
    <svg viewBox="0 0 520 380" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
      {/* Browser frame */}
      <rect x="0" y="0" width="520" height="380" rx="16" fill="rgba(255,255,255,0.06)" stroke="rgba(0,255,255,0.2)" strokeWidth="1"/>
      {/* Title bar */}
      <rect x="0" y="0" width="520" height="36" rx="16" fill="rgba(0,255,255,0.06)"/>
      <rect x="0" y="20" width="520" height="16" fill="rgba(0,255,255,0.06)"/>
      <circle cx="20" cy="18" r="5" fill="#FF5F57"/>
      <circle cx="38" cy="18" r="5" fill="#FEBC2E"/>
      <circle cx="56" cy="18" r="5" fill="#28C840"/>
      {/* Sidebar */}
      <rect x="0" y="36" width="110" height="344" fill="rgba(0,0,255,0.15)"/>
      <rect x="16" y="56" width="78" height="8" rx="4" fill="rgba(0,255,255,0.3)"/>
      <rect x="16" y="80" width="60" height="6" rx="3" fill="rgba(255,255,255,0.15)"/>
      <rect x="16" y="100" width="68" height="6" rx="3" fill="rgba(255,255,255,0.1)"/>
      <rect x="16" y="120" width="54" height="6" rx="3" fill="rgba(255,255,255,0.1)"/>
      <rect x="16" y="140" width="72" height="6" rx="3" fill="rgba(255,255,255,0.1)"/>
      {/* Stats cards */}
      <rect x="126" y="50" width="115" height="60" rx="8" fill="rgba(0,255,255,0.08)" stroke="rgba(0,255,255,0.15)" strokeWidth="0.5"/>
      <rect x="140" y="64" width="40" height="6" rx="3" fill="rgba(0,255,255,0.4)"/>
      <rect x="140" y="80" width="70" height="12" rx="4" fill="rgba(255,255,255,0.2)"/>
      <rect x="257" y="50" width="115" height="60" rx="8" fill="rgba(0,255,255,0.08)" stroke="rgba(0,255,255,0.15)" strokeWidth="0.5"/>
      <rect x="271" y="64" width="50" height="6" rx="3" fill="rgba(0,255,255,0.4)"/>
      <rect x="271" y="80" width="60" height="12" rx="4" fill="rgba(255,255,255,0.2)"/>
      <rect x="388" y="50" width="115" height="60" rx="8" fill="rgba(0,255,255,0.08)" stroke="rgba(0,255,255,0.15)" strokeWidth="0.5"/>
      <rect x="402" y="64" width="45" height="6" rx="3" fill="rgba(0,255,255,0.4)"/>
      <rect x="402" y="80" width="55" height="12" rx="4" fill="rgba(255,255,255,0.2)"/>
      {/* Car cards */}
      <rect x="126" y="130" width="175" height="110" rx="8" fill="rgba(255,255,255,0.05)" stroke="rgba(0,255,255,0.1)" strokeWidth="0.5"/>
      <rect x="134" y="138" width="159" height="60" rx="6" fill="rgba(0,0,255,0.12)"/>
      {/* Car silhouette 1 */}
      <path d="M170 185 L180 172 L200 168 L220 168 L240 172 L250 180 L270 182 L270 190 L265 192 L260 188 L252 188 L248 192 L200 192 L196 188 L188 188 L184 192 L170 192 Z" fill="rgba(0,255,255,0.25)" stroke="rgba(0,255,255,0.4)" strokeWidth="0.5"/>
      <rect x="134" y="204" width="80" height="6" rx="3" fill="rgba(255,255,255,0.2)"/>
      <rect x="134" y="218" width="50" height="8" rx="4" fill="rgba(0,255,255,0.3)"/>
      <rect x="317" y="130" width="175" height="110" rx="8" fill="rgba(255,255,255,0.05)" stroke="rgba(0,255,255,0.1)" strokeWidth="0.5"/>
      <rect x="325" y="138" width="159" height="60" rx="6" fill="rgba(0,0,255,0.12)"/>
      {/* Car silhouette 2 */}
      <path d="M361 185 L371 172 L391 168 L411 168 L431 172 L441 180 L461 182 L461 190 L456 192 L451 188 L443 188 L439 192 L391 192 L387 188 L379 188 L375 192 L361 192 Z" fill="rgba(0,255,255,0.25)" stroke="rgba(0,255,255,0.4)" strokeWidth="0.5"/>
      <rect x="325" y="204" width="80" height="6" rx="3" fill="rgba(255,255,255,0.2)"/>
      <rect x="325" y="218" width="50" height="8" rx="4" fill="rgba(0,255,255,0.3)"/>
      {/* Chat panel */}
      <rect x="126" y="260" width="377" height="100" rx="8" fill="rgba(255,255,255,0.04)" stroke="rgba(0,255,255,0.1)" strokeWidth="0.5"/>
      <rect x="140" y="274" width="60" height="6" rx="3" fill="rgba(0,255,255,0.35)"/>
      {/* Chat bubbles */}
      <rect x="140" y="290" width="160" height="20" rx="10" fill="rgba(255,255,255,0.08)"/>
      <rect x="140" y="296" width="120" height="8" rx="4" fill="rgba(255,255,255,0.12)"/>
      <rect x="340" y="318" width="140" height="20" rx="10" fill="rgba(0,0,255,0.2)"/>
      <rect x="350" y="324" width="100" height="8" rx="4" fill="rgba(0,255,255,0.2)"/>
    </svg>
  );
}

function AgencySVG() {
  return (
    <svg viewBox="0 0 440 320" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
      {/* Building */}
      <rect x="80" y="60" width="280" height="220" rx="8" fill="rgba(0,0,255,0.08)" stroke="rgba(0,255,255,0.15)" strokeWidth="1"/>
      {/* Showroom windows */}
      <rect x="100" y="180" width="100" height="80" rx="4" fill="rgba(0,255,255,0.06)" stroke="rgba(0,255,255,0.15)" strokeWidth="0.5"/>
      <rect x="240" y="180" width="100" height="80" rx="4" fill="rgba(0,255,255,0.06)" stroke="rgba(0,255,255,0.15)" strokeWidth="0.5"/>
      {/* Sign */}
      <rect x="140" y="80" width="160" height="30" rx="6" fill="rgba(0,0,255,0.2)" stroke="rgba(0,255,255,0.2)" strokeWidth="0.5"/>
      <rect x="165" y="91" width="110" height="8" rx="4" fill="rgba(0,255,255,0.4)"/>
      {/* Cars */}
      <path d="M115 245 L125 232 L145 228 L165 228 L180 232 L188 240 L195 242 L195 250 L190 252 L185 248 L178 248 L175 252 L140 252 L137 248 L130 248 L127 252 L115 252 Z" fill="rgba(0,255,255,0.2)" stroke="rgba(0,255,255,0.35)" strokeWidth="0.5"/>
      <path d="M255 245 L265 232 L285 228 L305 228 L320 232 L328 240 L335 242 L335 250 L330 252 L325 248 L318 248 L315 252 L280 252 L277 248 L270 248 L267 252 L255 252 Z" fill="rgba(0,255,255,0.2)" stroke="rgba(0,255,255,0.35)" strokeWidth="0.5"/>
      {/* Digital overlay lines */}
      <line x1="80" y1="140" x2="360" y2="140" stroke="rgba(0,255,255,0.08)" strokeWidth="0.5" strokeDasharray="4 4"/>
      {/* Floating data points */}
      <circle cx="120" cy="120" r="12" fill="rgba(0,255,255,0.1)" stroke="rgba(0,255,255,0.25)" strokeWidth="0.5"/>
      <circle cx="120" cy="120" r="3" fill={T.cyan}/>
      <circle cx="320" cy="100" r="12" fill="rgba(0,255,255,0.1)" stroke="rgba(0,255,255,0.25)" strokeWidth="0.5"/>
      <circle cx="320" cy="100" r="3" fill={T.cyan}/>
      <circle cx="220" cy="150" r="12" fill="rgba(0,255,255,0.1)" stroke="rgba(0,255,255,0.25)" strokeWidth="0.5"/>
      <circle cx="220" cy="150" r="3" fill={T.cyan}/>
      {/* Connection lines */}
      <line x1="132" y1="120" x2="208" y2="150" stroke="rgba(0,255,255,0.15)" strokeWidth="0.5"/>
      <line x1="232" y1="150" x2="308" y2="100" stroke="rgba(0,255,255,0.15)" strokeWidth="0.5"/>
    </svg>
  );
}

/* ═══════════════════════════════════════════
   GRID BACKGROUND (CSS-only)
   ═══════════════════════════════════════════ */
const gridBgStyle = {
  backgroundImage: `
    linear-gradient(rgba(0,255,255,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,255,255,0.03) 1px, transparent 1px)
  `,
  backgroundSize: '60px 60px',
};

/* ═══════════════════════════════════════════
   GLASSCARD
   ═══════════════════════════════════════════ */
function GlassCard({ children, className = "", hover = true, ...props }) {
  return (
    <div
      className={`rounded-2xl p-6 md:p-8 ${className}`}
      style={{
        background: T.cardBg,
        border: `1px solid ${T.cardBorder}`,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        transition: hover ? 'border-color 0.3s, box-shadow 0.3s, transform 0.3s' : undefined,
      }}
      onMouseEnter={hover ? (e) => {
        e.currentTarget.style.borderColor = 'rgba(0,255,255,0.35)';
        e.currentTarget.style.boxShadow = '0 0 30px rgba(0,255,255,0.06)';
        e.currentTarget.style.transform = 'translateY(-4px)';
      } : undefined}
      onMouseLeave={hover ? (e) => {
        e.currentTarget.style.borderColor = T.cardBorder;
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      } : undefined}
      {...props}
    >
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({ nombre: '', agencia: '', whatsapp: '', correo: '', mensaje: '' });
  const [formState, setFormState] = useState('idle'); // idle | sending | sent | error
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = useCallback((id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const validateForm = () => {
    const errs = {};
    if (!formData.nombre.trim()) errs.nombre = 'Ingresá tu nombre';
    if (!formData.whatsapp.trim()) errs.whatsapp = 'Ingresá tu WhatsApp';
    if (!formData.correo.trim()) errs.correo = 'Ingresá tu correo';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.correo)) errs.correo = 'Correo inválido';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setFormState('sending');
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error();
      setFormState('sent');
    } catch {
      setFormState('error');
      setTimeout(() => setFormState('idle'), 4000);
    }
  };

  const navLinks = [
    { label: "Funciones", id: "funciones" },
    { label: "Cómo funciona", id: "como-funciona" },
    { label: "Para agencias", id: "para-agencias" },
  ];

  return (
    <div style={{ background: T.bg, fontFamily: T.font.body, color: T.white }} className="min-h-screen overflow-x-hidden">

      {/* ═══ HEADER ═══ */}
      <header
        data-testid="landing-header"
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? 'rgba(5,10,48,0.85)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(0,255,255,0.08)' : '1px solid transparent',
        }}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-16 md:h-[72px]">
          <button onClick={() => scrollTo('inicio')} className="flex items-center gap-2" data-testid="nav-logo">
            <img src="/logo.png" alt="AutoFlow" className="h-9 w-auto rounded-lg" />
            <span style={{ fontFamily: T.font.display, fontWeight: 800, fontSize: '1.5rem', letterSpacing: '-0.02em' }}>
              Auto<span style={{ color: T.cyan }}>Flow</span>
            </span>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-7" data-testid="desktop-nav">
            <Link to="/autos-flow" className="text-sm font-bold transition-colors relative pr-1" style={{ fontFamily: T.font.heading, color: T.cyan }} data-testid="nav-autos-flow">
              Autos - Flow
              <span className="absolute -top-2.5 -right-1 text-[11px] pointer-events-none leading-none" aria-hidden="true">🔥</span>
            </Link>
            {navLinks.map(l => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                className="text-sm transition-colors"
                style={{ fontFamily: T.font.body, fontWeight: 500, color: T.textMuted }}
                onMouseEnter={e => e.currentTarget.style.color = T.white}
                onMouseLeave={e => e.currentTarget.style.color = T.textMuted}
                data-testid={`nav-${l.id}`}
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => scrollTo('contacto')}
              className="text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-300"
              style={{
                fontFamily: T.font.heading,
                border: `1.5px solid ${T.cyan}`,
                color: T.cyan,
                boxShadow: '0 0 16px rgba(0,255,255,0.08)',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = T.cyan; e.currentTarget.style.color = T.bg; e.currentTarget.style.boxShadow = '0 0 32px rgba(0,255,255,0.25)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.cyan; e.currentTarget.style.boxShadow = '0 0 16px rgba(0,255,255,0.08)'; }}
              data-testid="nav-agendar-demo"
            >
              Agendar Demo
            </button>
          </nav>

          {/* Mobile */}
          <button className="md:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
            {menuOpen ? <X size={24} color={T.white} /> : <Menu size={24} color={T.white} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden"
            style={{ background: 'rgba(5,10,48,0.95)', backdropFilter: 'blur(20px)', borderBottom: `1px solid ${T.cardBorder}` }}
            data-testid="mobile-menu"
          >
            <div className="px-5 py-5 space-y-3">
              <Link to="/autos-flow" className="block w-full text-left py-2 font-bold" style={{ color: T.cyan }} data-testid="mobile-nav-autos-flow">Autos - Flow</Link>
              {navLinks.map(l => (
                <button key={l.id} onClick={() => scrollTo(l.id)} className="block w-full text-left py-2 font-medium" style={{ color: T.textLight }} data-testid={`mobile-nav-${l.id}`}>{l.label}</button>
              ))}
              <Link to="/agencias" className="block w-full text-left py-2 font-medium" style={{ color: T.textLight }} data-testid="mobile-nav-agencias">Agencias</Link>
              <Link to="/app/login" className="block w-full text-left py-2 font-medium" style={{ color: T.textMuted }} data-testid="mobile-nav-ingresar">Ingresar</Link>
              <button onClick={() => scrollTo('contacto')} className="block w-full text-center py-3 rounded-full font-semibold mt-2" style={{ background: T.cyan, color: T.bg }} data-testid="mobile-nav-demo">Agendar Demo</button>
            </div>
          </motion.div>
        )}
      </header>

      {/* ═══ HERO ═══ */}
      <section
        id="inicio"
        className="relative min-h-screen flex items-center pt-20"
        style={{ ...gridBgStyle, background: `linear-gradient(160deg, ${T.bg} 0%, #060E40 40%, rgba(0,0,255,0.15) 80%, ${T.bg} 100%)` }}
        data-testid="hero-section"
      >
        {/* Radial glow */}
        <div className="absolute top-0 right-0 w-[60%] h-[80%] pointer-events-none" style={{ background: 'radial-gradient(ellipse at 70% 30%, rgba(0,0,255,0.12) 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 left-0 w-[40%] h-[50%] pointer-events-none" style={{ background: 'radial-gradient(ellipse at 20% 80%, rgba(0,255,255,0.06) 0%, transparent 60%)' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            {/* Left: Copy */}
            <div>
              <Reveal>
                <span
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs tracking-[0.2em] uppercase mb-6"
                  style={{ fontFamily: T.font.display, fontWeight: 600, background: 'rgba(0,255,255,0.08)', border: `1px solid rgba(0,255,255,0.2)`, color: T.cyan }}
                  data-testid="hero-badge"
                >
                  <span style={{ fontSize: '10px' }}>&#10022;</span> Impulsado por Inteligencia Artificial
                </span>
              </Reveal>

              <Reveal delay={0.08}>
                <h1
                  className="text-4xl sm:text-5xl lg:text-[4.5rem] leading-[0.95] tracking-tighter mb-6"
                  style={{ fontFamily: T.font.display, fontWeight: 800 }}
                  data-testid="hero-title"
                >
                  La plataforma inteligente para vender más autos
                </h1>
              </Reveal>

              <Reveal delay={0.14}>
                <p className="text-base md:text-lg leading-relaxed mb-10 max-w-lg" style={{ color: T.textMuted }}>
                  AutoFlow digitaliza tu agencia, automatiza la atención y conecta a tus vendedores con los compradores correctos — todo desde un solo lugar.
                </p>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => scrollTo('contacto')}
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-semibold transition-all duration-300"
                    style={{ fontFamily: T.font.heading, background: T.cyan, color: T.bg, boxShadow: '0 0 24px rgba(0,255,255,0.2)' }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 48px rgba(0,255,255,0.35)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = '0 0 24px rgba(0,255,255,0.2)'}
                    data-testid="hero-cta-demo"
                  >
                    Agendar una Demo <ArrowRight size={18} />
                  </button>
                  <button
                    onClick={() => scrollTo('como-funciona')}
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-medium transition-all duration-300"
                    style={{ border: '1.5px solid rgba(255,255,255,0.2)', color: T.white }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.background = 'transparent'; }}
                    data-testid="hero-cta-how"
                  >
                    Ver cómo funciona <ChevronRight size={18} />
                  </button>
                </div>
              </Reveal>

              {/* Stats */}
              <Reveal delay={0.28}>
                <div className="flex flex-wrap gap-6 md:gap-10 mt-14">
                  {[
                    { val: "120+", label: "Agencias activas" },
                    { val: "98%", label: "Satisfacción" },
                    { val: "LATAM", label: "Ready" },
                  ].map((s, i) => (
                    <div key={i} className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold" style={{ fontFamily: T.font.display, fontWeight: 800, color: T.cyan }}>{s.val}</span>
                      <span className="text-xs uppercase tracking-widest" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.textMuted }}>{s.label}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            {/* Right: Dashboard SVG */}
            <Reveal delay={0.15} className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-8 rounded-3xl pointer-events-none" style={{ background: 'radial-gradient(circle at center, rgba(0,0,255,0.15) 0%, transparent 70%)' }} />
                <DashboardSVG />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ PROBLEM ═══ */}
      <section className="py-20 md:py-28" style={{ background: T.bgAlt }} data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight" style={{ fontFamily: T.font.display, fontWeight: 800 }}>
                Las agencias pierden ventas por falta de respuesta rápida
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: Clock, title: "Consultas sin respuesta", desc: "Los clientes preguntan a cualquier hora y no reciben atención inmediata. Cada minuto sin responder es una venta que se va." },
              { icon: ClipboardList, title: "Inventario desactualizado", desc: "Sin control centralizado, mostrás autos que ya no tenés. Tus clientes pierden la confianza." },
              { icon: TrendingDown, title: "Información dispersa", desc: "Fotos, precios y detalles repartidos entre WhatsApp, papel y hojas de cálculo. Caos operativo constante." },
            ].map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <GlassCard data-testid={`problem-card-${i}`}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(0,255,255,0.08)' }}>
                    <p.icon size={22} color={T.cyan} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-bold mb-3 tracking-tight" style={{ fontFamily: T.font.heading, fontWeight: 700 }}>{p.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: T.textMuted }}>{p.desc}</p>
                </GlassCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="como-funciona" className="py-20 md:py-28" style={{ background: T.bg }} data-testid="how-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-[0.25em] mb-3 inline-block" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.cyan }}>Cómo funciona</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight" style={{ fontFamily: T.font.display, fontWeight: 800 }}>
                Tres pasos para digitalizar tu agencia
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connector line */}
            <div className="hidden md:block absolute top-16 left-[16.66%] right-[16.66%] h-px" style={{ background: `linear-gradient(90deg, transparent, ${T.cyan}33, transparent)` }} />

            {[
              { num: "01", title: "Cargás tu inventario", desc: "Subís tus autos con fotos, descripción y precio en minutos. Todo centralizado.", icon: Upload },
              { num: "02", title: "La IA atiende a tus clientes", desc: "Tu agente responde consultas 24/7, comparte fotos y agenda visitas automáticamente.", icon: Bot },
              { num: "03", title: "Vos cerrás la venta", desc: "Recibís los leads calificados y te enfocás en lo que mejor hacés: vender.", icon: Car },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="text-center relative" data-testid={`step-${i}`}>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5 relative z-10" style={{ background: 'rgba(0,0,255,0.2)', border: `1px solid rgba(0,255,255,0.2)` }}>
                    <s.icon size={26} color={T.cyan} strokeWidth={1.5} />
                  </div>
                  <span className="text-xs tracking-[0.3em] uppercase mb-2 block" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.cyan }}>{s.num}</span>
                  <h3 className="text-xl font-bold mb-3 tracking-tight" style={{ fontFamily: T.font.heading, fontWeight: 700 }}>{s.title}</h3>
                  <p className="text-sm leading-relaxed max-w-xs mx-auto" style={{ color: T.textMuted }}>{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section id="funciones" className="py-20 md:py-28" style={{ background: T.bgAlt }} data-testid="features-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal>
            <div className="text-center mb-14">
              <span className="text-xs uppercase tracking-[0.25em] mb-3 inline-block" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.cyan }}>Funcionalidades</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight" style={{ fontFamily: T.font.display, fontWeight: 800 }}>
                Todo lo que tu agencia necesita, en un solo sistema
              </h2>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Car, title: "Gestión de inventario", desc: "Publicá, editá y archivá vehículos con fotos de alta calidad, specs técnicos y precios actualizados." },
              { icon: Bot, title: "Agente IA 24/7", desc: "Responde consultas, envía fotos del stock, informa precios y agenda visitas — todo automático." },
              { icon: Globe, title: "Página de agencia", desc: "Tu vitrina digital personalizada con tu catálogo completo en tiempo real, accesible desde cualquier dispositivo." },
              { icon: BarChart3, title: "Panel de control", desc: "Métricas de consultas, vehículos más vistos, leads recibidos y rendimiento general de tu agencia." },
              { icon: Smartphone, title: "Multi-dispositivo", desc: "Gestioná tu agencia desde cualquier lugar: celular, tablet o computadora, sin instalar nada." },
              { icon: Shield, title: "Datos seguros", desc: "Tu información y la de tus clientes, protegida con encriptación y respaldada en la nube." },
            ].map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <GlassCard className="h-full" data-testid={`feature-card-${i}`}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(0,255,255,0.08)' }}>
                    <f.icon size={22} color={T.cyan} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-bold mb-2 tracking-tight" style={{ fontFamily: T.font.heading, fontWeight: 700 }}>{f.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: T.textMuted }}>{f.desc}</p>
                </GlassCard>
              </Reveal>
            ))}
          </div>

          {/* Autos - Flow mention */}
          <Reveal>
            <div className="mt-10 rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row items-center gap-5 sm:gap-8" style={{ background: 'rgba(0,255,255,0.04)', border: `1px solid ${T.cardBorder}` }} data-testid="autosflow-mention">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(0,255,255,0.1)' }}>
                <Globe size={26} color={T.cyan} strokeWidth={1.5} />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-lg font-bold mb-1" style={{ fontFamily: T.font.heading }}>Autos - Flow</h3>
                <p className="text-sm" style={{ color: T.textMuted }}>Tus autos, visibles en nuestra red publica de vehiculos. Mas exposicion, mas ventas.</p>
              </div>
              <Link to="/autos-flow" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold transition-all hover:brightness-110 flex-shrink-0" style={{ background: T.cyan, color: T.bg }} data-testid="explore-autosflow">
                Explorar <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ FOR AGENCIES ═══ */}
      <section id="para-agencias" className="py-20 md:py-28" style={{ background: T.bg }} data-testid="agencies-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-6 rounded-3xl pointer-events-none" style={{ background: 'radial-gradient(circle at center, rgba(0,255,255,0.06) 0%, transparent 70%)' }} />
                <AgencySVG />
              </div>
            </Reveal>

            <div>
              <Reveal>
                <span className="text-xs uppercase tracking-[0.25em] mb-3 inline-block" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.cyan }}>Para agencias</span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4" style={{ fontFamily: T.font.display, fontWeight: 800 }}>
                  Diseñado para agencias que quieren crecer
                </h2>
                <p className="text-base leading-relaxed mb-10" style={{ color: T.textMuted }}>
                  Ya seas una agencia con 10 autos o un concesionario multi-sucursal, AutoFlow se adapta a tu operación y escala con vos.
                </p>
              </Reveal>

              <div className="grid gap-5">
                {[
                  { icon: FileX, title: "Sin papeles", desc: "Toda la operación digital. Inventario, leads y comunicación en un solo lugar." },
                  { icon: Wrench, title: "Sin técnicos", desc: "Fácil de usar desde el primer día. Sin instalaciones, sin configuraciones complejas." },
                  { icon: Rocket, title: "Sin límites", desc: "Escalable a tu ritmo. Agregá vehículos, usuarios y sucursales cuando los necesites." },
                ].map((b, i) => (
                  <Reveal key={i} delay={i * 0.08}>
                    <GlassCard className="flex items-start gap-4" data-testid={`benefit-${i}`}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(0,255,255,0.08)' }}>
                        <b.icon size={20} color={T.cyan} strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="text-base font-bold mb-1" style={{ fontFamily: T.font.heading, fontWeight: 700 }}>{b.title}</h3>
                        <p className="text-sm leading-relaxed" style={{ color: T.textMuted }}>{b.desc}</p>
                      </div>
                    </GlassCard>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CTA / CONTACT ═══ */}
      <section
        id="contacto"
        className="py-20 md:py-28 relative"
        style={{ background: `linear-gradient(160deg, ${T.bg} 0%, rgba(0,0,255,0.12) 50%, rgba(0,255,255,0.06) 100%)` }}
        data-testid="contact-section"
      >
        <div className="absolute inset-0 pointer-events-none" style={gridBgStyle} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left: Copy */}
            <div>
              <Reveal>
                <span className="text-xs uppercase tracking-[0.25em] mb-3 inline-block" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.cyan }}>Contacto</span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-5" style={{ fontFamily: T.font.display, fontWeight: 800 }}>
                  ¿Querés ver AutoFlow en acción?
                </h2>
                <p className="text-base leading-relaxed mb-8" style={{ color: T.textMuted }}>
                  Agendá una demo gratuita y te mostramos cómo funciona adaptado a tu agencia, sin compromisos.
                </p>
              </Reveal>

              <Reveal delay={0.1}>
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-6 py-3.5 rounded-full transition-all duration-300 mb-8"
                  style={{ background: 'rgba(37,211,102,0.15)', border: '1px solid rgba(37,211,102,0.3)', color: '#25D366' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(37,211,102,0.25)'; e.currentTarget.style.boxShadow = '0 0 24px rgba(37,211,102,0.15)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(37,211,102,0.15)'; e.currentTarget.style.boxShadow = 'none'; }}
                  data-testid="contact-whatsapp"
                >
                  <MessageCircle size={20} />
                  <span className="font-semibold text-sm" style={{ fontFamily: T.font.heading }}>Escribinos por WhatsApp</span>
                </a>
              </Reveal>

              <Reveal delay={0.15}>
                <p className="text-xs" style={{ color: T.textMuted }}>
                  Respondemos en menos de 24 horas. Sin contratos, sin letra chica.
                </p>
              </Reveal>
            </div>

            {/* Right: Form */}
            <Reveal delay={0.08}>
              <GlassCard hover={false} className="relative overflow-hidden" data-testid="contact-form-card">
                {formState === 'sent' ? (
                  <div className="text-center py-10" data-testid="form-success">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(0,255,255,0.1)', border: `1px solid ${T.cyan}` }}>
                      <Send size={24} color={T.cyan} />
                    </div>
                    <h3 className="text-xl font-bold mb-2" style={{ fontFamily: T.font.heading }}>Solicitud enviada</h3>
                    <p className="text-sm" style={{ color: T.textMuted }}>Te contactaremos pronto para coordinar tu demo.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <h3 className="text-lg font-bold mb-6" style={{ fontFamily: T.font.heading }}>Agendar mi Demo Gratuita</h3>
                    {[
                      { key: 'nombre', label: 'Nombre *', type: 'text', placeholder: 'Tu nombre completo' },
                      { key: 'agencia', label: 'Agencia', type: 'text', placeholder: 'Nombre de tu agencia (opcional)' },
                      { key: 'whatsapp', label: 'WhatsApp *', type: 'tel', placeholder: '+54 9 261 XXX XXXX' },
                      { key: 'correo', label: 'Correo *', type: 'email', placeholder: 'tu@correo.com' },
                    ].map(f => (
                      <div key={f.key} className="mb-4">
                        <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.textMuted }}>{f.label}</label>
                        <input
                          type={f.type}
                          placeholder={f.placeholder}
                          value={formData[f.key]}
                          onChange={e => setFormData(p => ({ ...p, [f.key]: e.target.value }))}
                          className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                          style={{
                            fontFamily: T.font.body,
                            background: 'rgba(255,255,255,0.04)',
                            border: formErrors[f.key] ? '1px solid #ff4444' : `1px solid ${T.cardBorder}`,
                            color: T.white,
                          }}
                          onFocus={e => e.currentTarget.style.borderColor = T.cyan}
                          onBlur={e => e.currentTarget.style.borderColor = formErrors[f.key] ? '#ff4444' : T.cardBorder}
                          data-testid={`form-${f.key}`}
                        />
                        {formErrors[f.key] && <span className="text-xs mt-1 block" style={{ color: '#ff4444' }}>{formErrors[f.key]}</span>}
                      </div>
                    ))}
                    <div className="mb-6">
                      <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.textMuted }}>Mensaje (opcional)</label>
                      <textarea
                        rows={3}
                        placeholder="Contanos sobre tu agencia..."
                        value={formData.mensaje}
                        onChange={e => setFormData(p => ({ ...p, mensaje: e.target.value }))}
                        className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200 resize-none"
                        style={{ fontFamily: T.font.body, background: 'rgba(255,255,255,0.04)', border: `1px solid ${T.cardBorder}`, color: T.white }}
                        onFocus={e => e.currentTarget.style.borderColor = T.cyan}
                        onBlur={e => e.currentTarget.style.borderColor = T.cardBorder}
                        data-testid="form-mensaje"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={formState === 'sending'}
                      className="w-full py-3.5 rounded-full text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                      style={{
                        fontFamily: T.font.heading,
                        background: formState === 'sending' ? 'rgba(0,255,255,0.5)' : T.cyan,
                        color: T.bg,
                        boxShadow: '0 0 20px rgba(0,255,255,0.15)',
                      }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 40px rgba(0,255,255,0.3)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 0 20px rgba(0,255,255,0.15)'}
                      data-testid="form-submit"
                    >
                      {formState === 'sending' ? 'Enviando...' : 'Agendar mi Demo Gratuita'}
                      {formState !== 'sending' && <ArrowRight size={16} />}
                    </button>
                    {formState === 'error' && <p className="text-xs text-center mt-3" style={{ color: '#ff4444' }}>Hubo un error. Intentá de nuevo o escribinos por WhatsApp.</p>}
                  </form>
                )}
              </GlassCard>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{ background: T.bgDeep }} className="pt-16 pb-8" data-testid="footer-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 pb-12" style={{ borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
            {/* Brand */}
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="AutoFlow" className="h-8 w-auto rounded-lg" />
                <span className="text-xl" style={{ fontFamily: T.font.display, fontWeight: 800 }}>
                  Auto<span style={{ color: T.cyan }}>Flow</span>
                </span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: T.textMuted }}>
                La plataforma de gestión integral para agencias de automóviles en Latinoamérica.
              </p>
            </div>

            {/* Navigation */}
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] mb-4" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.textLight }}>Navegación</h4>
              <ul className="space-y-2.5">
                {[{ l: "Inicio", id: "inicio" }, ...navLinks].map(n => (
                  <li key={n.id}><button onClick={() => scrollTo(n.id)} className="text-sm transition-colors" style={{ color: T.textMuted }} onMouseEnter={e => e.currentTarget.style.color = T.white} onMouseLeave={e => e.currentTarget.style.color = T.textMuted} data-testid={`footer-nav-${n.id}`}>{n.l || n.label}</button></li>
                ))}
                <li><Link to="/autos-flow" className="text-sm font-semibold transition-colors" style={{ color: T.cyan }} data-testid="footer-autos-flow">Autos - Flow</Link></li>
                <li><Link to="/agencias" className="text-sm transition-colors" style={{ color: T.textMuted }} onMouseEnter={e => e.currentTarget.style.color = T.white} onMouseLeave={e => e.currentTarget.style.color = T.textMuted} data-testid="footer-agencias">Agencias</Link></li>
                <li><Link to="/app/login" className="text-sm transition-colors" style={{ color: T.textMuted }} onMouseEnter={e => e.currentTarget.style.color = T.white} onMouseLeave={e => e.currentTarget.style.color = T.textMuted} data-testid="footer-ingresar">Ingresar</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] mb-4" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.textLight }}>Legal</h4>
              <ul className="space-y-2.5">
                <li><Link to="/privacy" className="text-sm transition-colors" style={{ color: T.textMuted }} onMouseEnter={e => e.currentTarget.style.color = T.white} onMouseLeave={e => e.currentTarget.style.color = T.textMuted} data-testid="footer-privacy">Política de Privacidad</Link></li>
                <li><Link to="/terms" className="text-sm transition-colors" style={{ color: T.textMuted }} onMouseEnter={e => e.currentTarget.style.color = T.white} onMouseLeave={e => e.currentTarget.style.color = T.textMuted} data-testid="footer-terms">Términos y Condiciones</Link></li>
              </ul>
            </div>

            {/* Social */}
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] mb-4" style={{ fontFamily: T.font.display, fontWeight: 600, color: T.textLight }}>Redes</h4>
              <div className="flex gap-3">
                {/* Instagram SVG */}
                <a href="#" className="w-9 h-9 rounded-lg flex items-center justify-center transition-all" style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid rgba(255,255,255,0.08)` }} onMouseEnter={e => e.currentTarget.style.borderColor = T.cyan} onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'} data-testid="footer-instagram" aria-label="Instagram">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="5" /><circle cx="17.5" cy="6.5" r="1.5" fill={T.textMuted} stroke="none" /></svg>
                </a>
                {/* LinkedIn SVG */}
                <a href="#" className="w-9 h-9 rounded-lg flex items-center justify-center transition-all" style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid rgba(255,255,255,0.08)` }} onMouseEnter={e => e.currentTarget.style.borderColor = T.cyan} onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'} data-testid="footer-linkedin" aria-label="LinkedIn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" /></svg>
                </a>
              </div>
            </div>
          </div>

          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs" style={{ color: T.textMuted }}>
              &copy; {new Date().getFullYear()} AutoFlow. Todos los derechos reservados. Hecho para Latinoamérica.
            </p>
            <div className="flex items-center gap-4 text-xs" style={{ color: T.textMuted }}>
              <Link to="/privacy" className="transition-colors" onMouseEnter={e => e.currentTarget.style.color = T.white} onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>Privacidad</Link>
              <span style={{ color: 'rgba(255,255,255,0.15)' }}>|</span>
              <Link to="/terms" className="transition-colors" onMouseEnter={e => e.currentTarget.style.color = T.white} onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>Términos</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
